import { useMemo } from 'react';
import { Helmet } from "react-helmet";

type JsonLdNode = Record<string, any>;
type JsonLdGraph = { "@context": "https://schema.org"; "@graph": JsonLdNode[] };

function toHHMM(time?: string): string {
  if (!time) return "";

  const match = time.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
  if (!match) return "";

  let [ , hStr, mStr, ampm ] = match;
  let h = parseInt(hStr, 10);
  let m = mStr || "00";

  if (ampm) {
    ampm = ampm.toUpperCase();
    if (ampm === "PM" && h < 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
  }

  return `${String(h).padStart(2, "0")}:${m}`;
}

function buildBranchPageBreadcrumbList(templateData: any, isOverallPage?: boolean,
  isStatePage?: boolean, isBranchPage?: boolean) {

  const stateName = templateData?.statePageDetails?.stateName ?? templateData?.branchPageDetails?.fullStateName;
  const stateUrl = process.env.REACT_APP_WEB_URL + "/visitor/" + templateData?.location?.path.slice(0, 2).join("/");

  const branchName = templateData?.branchPageDetails?.displayName;
  const branchUrl = process.env.REACT_APP_WEB_URL + "/visitor/" + templateData?.location?.path.slice(0, 3).join("/");

  const items: { name: string; item: string }[] = [];

  items.push({ name: "Home", item: `${process.env.REACT_APP_WEB_URL}/visitor` });

  if (isOverallPage || isStatePage || isBranchPage) {
    items.push({ name: "Lansing Branches", item: `${process.env.REACT_APP_WEB_URL}` + `/visitor` + "/lansing-branches" });
  }

  if (isStatePage || isBranchPage) {
    items.push({ name: stateName, item: stateUrl });
  }
  
  if (isBranchPage) {
    items.push({ name: branchName, item: branchUrl });
  }

  return {
    "@type": "BreadcrumbList",
    "itemListElement": items.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.item,
    })),
  };
}

function getOverallPageBranches(templateData: any) {
  const branchesObj = templateData?.overallPageDetails?.branches;

  if (!branchesObj) return [];

  return Object.values(branchesObj).map((branchArr: any) => {
    const firstBranch = (branchArr as any[])[0];
    return {
      stateName: firstBranch?.stateName,
      stateUrl: `${process.env.REACT_APP_WEB_URL}/visitor${firstBranch?.stateUrl || ''}`
    };
  });
}

function createBranchPagesJsonGraph(opts: {
  isOverallPage?: boolean;
  isStatePage?: boolean;
  isBranchPage?: boolean;
  breadcrumb: JsonLdNode;
  organization: JsonLdNode;
  statesItemList?: JsonLdNode;
  stateItemList?: JsonLdNode;
  branchItem?: JsonLdNode;
}): JsonLdGraph | undefined {
  const {
    isOverallPage,
    isStatePage,
    isBranchPage,
    breadcrumb,
    organization,
    statesItemList,
    stateItemList,
    branchItem,
  } = opts;

  const base = [breadcrumb, organization];

  if (isOverallPage) {
    return { "@context": "https://schema.org", "@graph": [...base, statesItemList!] };
  }
  if (isStatePage) {
    return { "@context": "https://schema.org", "@graph": [...base, stateItemList!] };
  }
  if (isBranchPage) {
    return { "@context": "https://schema.org", "@graph": [...base, branchItem!] };
  }
  return undefined;
}

const SeoBranchPages: React.FC<{ 
  templateData: any,
  isOverallPage?: boolean;
  isStatePage?: boolean;
  isBranchPage?: boolean;
}> = ({ templateData, isOverallPage, isStatePage, isBranchPage }) => {

  const allBranches = getOverallPageBranches(templateData);
  const breadcrumbBuild = buildBranchPageBreadcrumbList(templateData, isOverallPage, isStatePage, isBranchPage);

  // const CORPORATION = {
  //   "@type": "Corporation",
  //   "@id": `${process.env.REACT_APP_WEB_URL}/#corporation`,
  //   "name": `Lansing Building Products`,
  //   "founder": "Ted Lansing",
  //   "foundingDate": "1955",
  //   "url": `${process.env.REACT_APP_WEB_URL}`,
  //   "address": {
  //     "@type": "PostalAddress",
  //     "streetAddress": "2221 Edward Holland Drive, Suite 300",
  //     "addressLocality": "Richmond",
  //     "addressRegion": "VA",
  //     "postalCode": "23230",
  //     "addressCountry": "US"
  //   },
  //   "contactPoint": [
  //     {
  //       "@type": "ContactPoint",
  //       "telephone": "+1-804-266-8893",
  //       "email": "info@lansingbp.com",
  //       "contactType": "customer service",
  //       "areaServed": "US"
  //     }
  //   ],
  //   "sameAs": [
  //     "https://www.instagram.com/lansing_bp/",
  //     "https://www.facebook.com/lansingbuildingproducts/",
  //     "https://www.linkedin.com/company/lansing-building-products/",
  //     "https://www.youtube.com/channel/UC_5LR4NyFQ59X1CqyNEKN_Q"
  //   ]
  // } as const;

  const ORGANIZATION = {
    "@type": "Organization",
      "@id": `${process.env.REACT_APP_WEB_URL}/#organization`,
      "name": "Lansing Building Products",
      "founder": "Ted Lansing",
      "foundingDate": "1955",
      "url": `${process.env.REACT_APP_WEB_URL}/visitor`,
      "logo": "https://cdn.builder.io/api/v1/image/assets%2Fbd9dcf776ad049b7b73b797c6d7d2b61%2F9aa175b9aaa14ef0a8dce6253c1da220?width=112",
      "address": {
          "@type": "PostalAddress",
          "streetAddress": "2221 Edward Holland Drive, Suite 300",
          "addressLocality": "Richmond",
          "addressRegion": "VA",
          "postalCode": "23230",
          "addressCountry": "US"
      },
      "contactPoint": [{
              "@type": "ContactPoint",
              "telephone": "+1-804-266-8893",
              "email": "info@lansingbp.com",
              "contactType": "customer service",
              "areaServed": "US"
          }
      ],
      "sameAs": [
        "https://www.instagram.com/lansing_bp/", 
        "https://www.facebook.com/lansingbuildingproducts/", 
        "https://www.linkedin.com/company/lansing-building-products/", 
        "https://www.youtube.com/channel/UC_5LR4NyFQ59X1CqyNEKN_Q"
      ]
  } as const;

  const STATES_ITEM_LIST = {
    "@type": "ItemList",
    "@id": `${process.env.REACT_APP_WEB_URL}/visitor/lansing-branches#states`,
    "name": "Lansing Building Products - States",
    "itemListElement": allBranches.map((b: any, index: number) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "State",
        "name": `${b.stateName}`,
        "url": b.stateUrl,
      }
    }))
  };

  const STATE_ITEM_LIST = {
    "@type": "ItemList",
    "@id": `${process.env.REACT_APP_WEB_URL}/visitor/lansing-branches/${templateData?.statePageDetails?.stateName?.toLowerCase()}`,
    "itemListElement": (templateData?.statePageDetails?.branches || []).map((branch: any, index: number) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "LocalBusiness",
        "@id": `${process.env.REACT_APP_WEB_URL}/visitor${branch.url}`,
        "name": `Lansing Building Products – ${branch.branchName}`,
        "url": `${process.env.REACT_APP_WEB_URL}/visitor${branch.url}`,
        "branchOf": {
          "@id": `${process.env.REACT_APP_WEB_URL}/#organization`
        },
        "telephone": branch.phone,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": branch.address1,
          "addressLocality": branch.city,
          "addressRegion": branch.stateAbbreviation,
          "postalCode": branch.zip,
          "addressCountry": "US"
        },
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          ...(toHHMM(branch.workingHoursMessage?.workingHoursFrom) && { 
            opens: toHHMM(branch.workingHoursMessage?.workingHoursFrom) 
          }),
          ...(toHHMM(branch.workingHoursMessage?.workingHoursTo) && { 
            closes: toHHMM(branch.workingHoursMessage?.workingHoursTo) 
          })
        },
        "hasMap": `https://maps.google.com/?q=${encodeURIComponent(
          `${branch.address1}, ${branch.city}, ${branch.stateAbbreviation}, ${branch.zip}`
        )}`
      }
    }))
  };

  const BRANCH_ITEM = {
    "@type": "LocalBusiness",
    "@id": `${process.env.REACT_APP_WEB_URL}/visitor${templateData?.location?.pathname}`,
    "url": `${process.env.REACT_APP_WEB_URL}/visitor${templateData?.location?.pathname}`,
    "name": `Lansing Building Products – ${templateData?.branchPageDetails?.displayName}`,
    "telephone": templateData?.branchPageDetails?.contact?.phone,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": templateData?.branchPageDetails?.address1,
      "addressLocality": templateData?.branchPageDetails?.city,
      "addressRegion": templateData?.branchPageDetails?.state,
      "postalCode": templateData?.branchPageDetails?.zipCode,
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": templateData?.branchPageDetails?.location?.latitude,
      "longitude": templateData?.branchPageDetails?.location?.longitude
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      ...(toHHMM(templateData?.branchPageDetails?.workingHours?.workingHoursFrom) && { 
        opens: toHHMM(templateData?.branchPageDetails?.workingHours?.workingHoursFrom) 
        }),
      ...(toHHMM(templateData?.branchPageDetails?.workingHours?.workingHoursTo) && { 
        closes: toHHMM(templateData?.branchPageDetails?.workingHours?.workingHoursTo) 
      })
    },
    "branchOf": {
      "@id": `${process.env.REACT_APP_WEB_URL}/#organization`
    },
    "hasMap": `https://maps.google.com/?q=${encodeURIComponent(
      `${templateData?.branchPageDetails?.address1}, ${templateData?.branchPageDetails?.city}, ${templateData?.branchPageDetails?.state}, ${templateData?.branchPageDetails?.zipCode}`
    )}`
  };


  const jsonLdGraph = useMemo(
    () =>
      createBranchPagesJsonGraph({
        isOverallPage,
        isStatePage,
        isBranchPage,
        breadcrumb: breadcrumbBuild,
        organization: ORGANIZATION,
        statesItemList: STATES_ITEM_LIST,
        stateItemList: STATE_ITEM_LIST,
        branchItem: BRANCH_ITEM,
      }),
    [
      isOverallPage,
      isStatePage,
      isBranchPage,
      breadcrumbBuild,
      ORGANIZATION,
      STATES_ITEM_LIST,
      STATE_ITEM_LIST,
      BRANCH_ITEM,
    ]
  );

  if (isBranchPage && templateData?.location?.path?.[1] === "home-offices")
    return <></>;
  else
    return (<Helmet> 
            {jsonLdGraph && (
              <script type="application/ld+json">
                {JSON.stringify(jsonLdGraph)}
              </script>
            )}
          </Helmet>
    );
}

export default SeoBranchPages;
